/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import './CategoryNavigation.scss'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { RightArrowIcon } from '@/assets/icons'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { feedUrl } from '@/urls'
import { i18n } from '@/locale/i18n'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import useNavbarFlatGround from '@/hooks/useNavbarFlatGround'
import OverlayPanel from '@/components/OverlayPanel/OverlayPanel'
import { isValidFeedOrdering } from '@/util/feedQueryParams'
import { pathnameWithoutLocalePrefix } from '@/util/localePath'
import DrawOutline from '@/components/ui/draw-outline'
import ScrollArrow from '@/components/ui/scroll-arrow/ScrollArrow'
import Slider from '@/components/ui/slider/Slider'

const CategoryNavigation = ({ parent, categories, categoriesLoading = false, subcategories, fullPath = '', currentSubcategory, onTagSelect, activeTag, subcategoriesLoading = false }) => {
  const [animationParent] = useAutoAnimate()
  const location = useLocation()
  const navigate = useNavigate()
  const navRef = useRef(null)
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0, opacity: 0 })
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const isMobileSize = useIsMobileSize()
  const [showCategoryOverlay, setShowCategoryOverlay] = useState(false)
  const overlayRef = useRef(null)

  // Scroll arrows for the categories strip (shown only when it overflows)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollArrows = () => {
    const el = navRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }

  useEffect(() => {
    updateScrollArrows()
    // Re-measure on the next frame: fonts/layout may not be settled on first load
    const raf = requestAnimationFrame(updateScrollArrows)
    window.addEventListener('resize', updateScrollArrows)
    let observer
    if (navRef.current && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateScrollArrows)
      observer.observe(navRef.current)
    }
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', updateScrollArrows)
      if (observer) observer.disconnect()
    }
  }, [categories, categoriesLoading])

  const scrollNavByStep = direction => {
    navRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' })
  }

  const sliderSettings = {
    slidesPerView: 'auto',
    freeMode: true,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    touchRatio: 1,
    resistance: true,
    resistanceRatio: 0.85,
    spaceBetween: 0,
    grabCursor: true,
    preventClicks: false,
    preventClicksPropagation: false,
    slideToClickedSlide: false,
    allowTouchMove: true,
  }

  // Handle scroll events to show/hide navigation
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Determine scroll direction and update visibility
      if (currentScrollY > lastScrollY && currentScrollY > 150) {
        // Scrolling down and past threshold - hide nav
        setIsVisible(false)
      } else {
        // Scrolling up - show nav
        setIsVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [lastScrollY])

  useNavbarFlatGround(isVisible)

  // Effect to close overlay when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setShowCategoryOverlay(false)
      }
    }

    if (showCategoryOverlay) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCategoryOverlay])

  const updateSliderPosition = () => {
    if (navRef.current) {
      const activeNavLink = navRef.current.querySelector('.active-nav-link')

      if (activeNavLink) {
        const navRect = navRef.current.getBoundingClientRect()
        const activeRect = activeNavLink.getBoundingClientRect()

        setSliderStyle({
          left: activeRect.left - navRect.left + navRef.current.scrollLeft,
          width: activeRect.width,
          opacity: 1,
        })

        // Keep the active tab centered/in view within the horizontal strip —
        // on mobile too, so clicking a tab near the edge doesn't leave it
        // hidden behind the scroll instead of in evidence.
        // `activeRect`/`navRect` are viewport-relative (post-scroll) positions, so the
        // tab's actual position within the full scrollable content needs the container's
        // current scrollLeft added back in — otherwise the target position is off by
        // however far the strip is already scrolled, sending it the wrong way for chips
        // near either end of the list.
        const scrollContainer = navRef.current
        const activeAbsoluteLeft = activeRect.left - navRect.left + scrollContainer.scrollLeft
        const targetScrollLeft = activeAbsoluteLeft - navRect.width / 2 + activeRect.width / 2
        if (activeRect.left < navRect.left || activeRect.right > navRect.right) {
          scrollContainer.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })
        }
      } else {
        setSliderStyle({ left: 0, width: 0, opacity: 0 })
      }
    }
  }

  useEffect(() => {
    if (fullPath === '') {
      setSliderStyle({ left: 0, width: 0, opacity: 0 })
      return
    }
  }, [fullPath])

  useEffect(() => {
    updateSliderPosition()
  }, [location.pathname, location.search, isMobileSize])

  useEffect(() => {
    const handleResize = () => updateSliderPosition()

    window.addEventListener('resize', handleResize)
    const initialTimer = setTimeout(updateSliderPosition, 100)

    // NOTE: We intentionally do not recompute slider position on container
    // scroll. The slider's `left` is `activeRect.left - navRect.left +
    // scrollContainer.scrollLeft`, which is the active link's offset inside
    // the scrollable content and is invariant under scroll. The slider lives
    // inside the scroll container (`position: absolute` within the
    // `position: relative` nav), so it scrolls with the content for free.
    // Recomputing per scroll forced two `getBoundingClientRect` reads and a
    // setState every frame on mobile.

    return () => {
      clearTimeout(initialTimer)
      window.removeEventListener('resize', handleResize)
    }
  }, [isMobileSize])

  const renderSubCategories = () => {
    const subcat = []

    if (currentSubcategory) {
      if (currentSubcategory.tree && currentSubcategory.tree.length >= 3) {
        if (isMobileSize) {
          const overlayContent = (
            <div className="sub-categories flex flex-col gap-4 max-w-[250px]">
              {currentSubcategory.tree.map((cat, index) =>
                index < currentSubcategory.tree.length - 1 ? (
                  <NavLink
                    key={`tree-cat-${cat.id || index}`}
                    className="py-2 px-3 rounded-lg whitespace-nowrap text-Neutral-6 hover:text-[var(--color-brand-04)]"
                    to={`/markets/${currentSubcategory.tree
                      .slice(0, index + 1)
                      .map(c => c.slug)
                      .join('/')}`}
                    onClick={() => setShowCategoryOverlay(false)}>
                    {cat.title}
                  </NavLink>
                ) : null
              )}
            </div>
          )

          subcat.push(
            <div key="category-ellipsis" className="flex items-center text-[var(--color-brand-04)]" ref={overlayRef}>
              <OverlayPanel
                content={overlayContent}
                position="bottom-middle-up-left"
                showMode="click"
                hideOnClickOutside={true}
                hideOnLeave={false}
                hideDelay={200}
                className="!mt-4 border border-Neutral-3 border-solid !p-3"
                isVisible={showCategoryOverlay}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={i18n('markets.category_more')}
                  aria-expanded={showCategoryOverlay}
                  className="py-3 px-4 rounded-lg mr-4 h-min whitespace-break-spaces bg-surface-2 text-[var(--color-brand-04)] border border-[var(--color-brand-04)] border-solid cursor-pointer"
                  onClick={() => setShowCategoryOverlay(!showCategoryOverlay)}
                  onKeyDown={event => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    setShowCategoryOverlay(!showCategoryOverlay)
                  }}>
                  ...
                </div>
              </OverlayPanel>
              {subcategories && <RightArrowIcon width="5px" />}
            </div>
          )
        } else {
          // Render full tree explicitly on desktop
          currentSubcategory.tree.slice(0, -1).forEach((cat, index) => {
            subcat.push(
              <div
                key={`desktop-tree-cat-${cat.id || index}`}
                className="flex item-center text-[var(--color-brand-04)] mr-3">
                <NavLink
                  className="py-3 px-4 rounded-lg mr-4 h-min whitespace-nowrap bg-surface-2 text-Neutral-6 hover:bg-surface-2 hover:text-Neutral-6"
                  to={`/markets/${currentSubcategory.tree
                    .slice(0, index + 1)
                    .map(c => c.slug)
                    .join('/')}`}>
                  {cat.title}
                </NavLink>
                <RightArrowIcon width="5px" />
              </div>
            )
          })
        }
      }

      // Always show current subcategory
      subcat.push(
        <div
          className="flex item-center text-[var(--color-brand-04)] mr-3"
          key={`current-subcat-${currentSubcategory.id}`}>
          <NavLink
            className={navData =>
              navData.isActive
                ? 'py-3 px-4 rounded-lg mr-4 h-min whitespace-nowrap bg-surface-2 text-[var(--color-brand-04)] border border-[var(--color-brand-04)] border-solid'
                : 'py-3 px-4 rounded-lg mr-4 h-min whitespace-nowrap bg-surface-2 text-Neutral-6 hover:bg-surface-2 hover:text-Neutral-6'
            }
            to={`/markets/${currentSubcategory.tree?.map(c => c.slug).join('/') || ''}`}>
            {currentSubcategory.title}
          </NavLink>
          {subcategories && subcategories.length > 0 && <RightArrowIcon width="5px" />}
        </div>
      )
    }

    if (onTagSelect && subcategories && subcategories.length > 0) {
      const isAllActive = !activeTag
      subcat.push(
        <div
          key="tag-filter-all"
          role="button"
          tabIndex={0}
          aria-pressed={isAllActive}
          className={`subcat-pill py-3 px-4 rounded-lg h-min whitespace-nowrap flex cursor-pointer ${
            isAllActive
              ? 'subcat-pill--active bg-surface-2 hover:bg-surface-3 text-Neutral-6 border border-[var(--color-brand-04)] border-solid'
              : 'bg-surface-2 hover:bg-surface-3 text-Neutral-6 hover:text-Neutral-6'
          }`}
          onClick={() => onTagSelect(null)}
          onKeyDown={event => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            onTagSelect(null)
          }}>
          {!isAllActive && <DrawOutline className="subcat-pill__draw" rectClassName="subcat-pill__draw-rect" />}
          {i18n('markets.category_all')}
        </div>
      )
    }

    if (subcategories && subcategories.length > 0)
      subcategories.forEach(category => {
        if (onTagSelect) {
          const identifier = category.slug || category.id
          const isActive = activeTag === identifier
          subcat.push(
            <div
              key={`tag-filter-${category.id}`}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              className={`subcat-pill py-3 px-4 rounded-lg h-min whitespace-nowrap flex cursor-pointer ${
                isActive
                  ? 'subcat-pill--active bg-surface-2 hover:bg-surface-3 text-Neutral-6 border border-[var(--color-brand-04)] border-solid'
                  : 'bg-surface-2 hover:bg-surface-3 text-Neutral-6 hover:text-Neutral-6'
              }`}
              onClick={() => onTagSelect(identifier)}
              onKeyDown={event => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onTagSelect(identifier)
              }}>
              {!isActive && <DrawOutline className="subcat-pill__draw" rectClassName="subcat-pill__draw-rect" />}
              {category.title}
            </div>
          )
        } else {
          subcat.push(
            <NavLink
              key={`category-link-${category.id}`}
              className={navData =>
                navData.isActive
                  ? 'subcat-pill subcat-pill--active py-3 px-4 rounded-lg h-min whitespace-nowrap flex bg-surface-2 hover:bg-surface-3 text-Neutral-6 border border-[var(--color-brand-04)] border-solid'
                  : 'subcat-pill py-3 px-4 rounded-lg h-min whitespace-nowrap flex bg-surface-2 hover:bg-surface-3 text-Neutral-6 hover:text-Neutral-6'
              }
              to={`${fullPath}/${fullPath === '/markets' ? (category?.tree?.map(c => c.slug).join('/') || category?.slug) : category?.slug}/${category?.id}`}>
              <DrawOutline className="subcat-pill__draw" rectClassName="subcat-pill__draw-rect" />
              {category.title}
            </NavLink>
          )
        }
      })

    return subcat
  }

  const renderSkeletonNavItems = () => {
    const widths = [80, 70, 50, 90, 60, 75, 55]
    return (
      <div className="flex items-center category-navigation__main-categories">
        {widths.map((w, index) => (
          <div key={`skeleton-${index}`} className="md:py-2 py-3 px-4 first:pl-0">
            <div className="category-tab-content py-4">
              <div
                className="relative overflow-hidden h-4 rounded-md bg-surface-2"
                style={{ width: `${w}px` }}>
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-3/20 to-transparent"
                  style={{ animation: 'shimmer 1.5s infinite', transform: 'translateX(-100%)' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderCategoryNavItems = () => {
    const navItems = []
    let trendingLink = null
    let newestLink = null
    let allCategoryLink = null

    // Add virtual category links (Trending, Newest, All)
    // These are shortcuts to sorting filters
    const searchParams = new URLSearchParams(location.search)
    const rawOrdering = searchParams.get('ordering')
    /* An ordering no control offers is no ordering (the SSR copy of this
       component validates the same way), and the locale prefix is not part
       of the path — /pt-br/markets is the root feed too. */
    const orderingParam = isValidFeedOrdering(rawOrdering) ? rawOrdering : null
    // Remove trailing slash for comparison
    const cleanPath = pathnameWithoutLocalePrefix(location.pathname).replace(/\/$/, '')
    const isOnRoot = cleanPath === '' || cleanPath === '/markets'
    
    // Detect which virtual category is active
    const isOnTrending = isOnRoot && (!orderingParam || orderingParam === '-relevance')
    const isOnNewest = isOnRoot && orderingParam === '-created_on'
    const isOnVolume = isOnRoot && orderingParam === '-volume'

    /* The three virtual tabs are one pathname told apart by the query, which
       NavLink cannot see: it marked New and All both `aria-current="page"` on
       every /markets URL (and on category pages besides). They are plain
       Links carrying the ordering-based current state themselves, so exactly
       one tab claims to be current. */
    trendingLink = (
      <Link
        key="category-link-trending"
        className={
          isOnTrending
            ? 'md:py-2 py-3 pr-6 font-semibold active-nav-link inline-block whitespace-nowrap'
            : 'md:py-2 py-3 pr-6 font-semibold inline-block whitespace-nowrap'
        }
        aria-current={isOnTrending ? 'page' : undefined}
        to="/">
        <div className="category-tab-content py-4">{i18n('markets.category_trending')}</div>
      </Link>
    )

    newestLink = (
      <Link
        key="category-link-newest"
        className={
          isOnNewest
            ? 'md:py-2 py-3 pr-6 font-semibold active-nav-link inline-block whitespace-nowrap'
            : 'md:py-2 py-3 pr-6 font-semibold inline-block whitespace-nowrap'
        }
        aria-current={isOnNewest ? 'page' : undefined}
        to="/markets?ordering=-created_on">
        <div className="category-tab-content py-4">{i18n('markets.category_new')}</div>
      </Link>
    )

    allCategoryLink = (
      <Link
        key="category-link-all"
        className={
          isOnVolume
            ? 'md:py-2 py-3 pr-6 font-semibold active-nav-link inline-block whitespace-nowrap'
            : 'md:py-2 py-3 pr-6 font-semibold inline-block whitespace-nowrap'
        }
        aria-current={isOnVolume ? 'page' : undefined}
        to="/markets?ordering=-volume">
        <div className="category-tab-content py-4">{i18n('markets.category_all')}</div>
      </Link>
    )

    // Add category links
    if (categories && categories.length > 0) {
      categories.forEach(category => {
        // Skip the "All" category from backend (we're using our own now)
        const isAllCategory = !category.title || category.title === ''
        if (isAllCategory) {
          return
        }

        const displayTitle = category.title

        // Add categories to the list
        navItems.push(
          <li key={`category-item-${category.id}`}>
            <NavLink
              key={`category-link-${category.id}`}
              className={navData =>
                navData.isActive
                  ? 'md:py-2 py-3 px-6 font-semibold active-nav-link inline-block !whitespace-nowrap'
                  : 'md:py-2 py-3 px-6 font-semibold inline-block !whitespace-nowrap'
              }
              to={`/markets/${category.slug}/`}
              end={false}>
              <div className="category-tab-content py-4">{displayTitle}</div>
            </NavLink>
          </li>
        )
      })
    }

    return (
      <div className="flex items-center justify-center category-navigation__main-categories">
        {trendingLink}
        {newestLink}
        {allCategoryLink}
        {navItems.length > 0 && <ul className="flex list-none p-0 m-0  ">{navItems}</ul>}
      </div>
    )
  }

  return (
    <>
      <nav
        className={`category-navigation bg-[var(--color-surface-1)] ${
          !isVisible ? 'category-navigation--hidden -translate-y-full' : 'translate-y-0'
        }`}
        ref={animationParent}>
        <div className="relative">
          {canScrollLeft && <ScrollArrow direction={-1} onClick={() => scrollNavByStep(-1)} />}
          <div
            className="flex overflow-x-auto !px-4 md:!px-[30px] scrollbar-hide snap-x relative"
            style={{ WebkitOverflowScrolling: 'touch' }}
            onScroll={updateScrollArrows}
            ref={navRef}>
          {!categoriesLoading && categories && categories.length > 0 ? renderCategoryNavItems() : renderSkeletonNavItems()}
          {/* Sliding underline indicator — disabled by design request. Set to true to re-enable. */}
          {false && !categoriesLoading && categories && categories.length > 0 && (
            <div
              className="sliding-border absolute bottom-0 h-0.5 bg-[var(--color-brand-04)] transition-all duration-300 ease-in-out"
              style={{
                left: `${sliderStyle.left}px`,
                width: `${sliderStyle.width}px`,
                opacity: sliderStyle.opacity,
              }}
            />
          )}
          </div>
          {canScrollRight && <ScrollArrow direction={1} onClick={() => scrollNavByStep(1)} />}
        </div>
      </nav>
      {(() => {
        const subcats = renderSubCategories()
        const hasSubcategories = subcats.length > 0
        const shouldShowSubcategoriesSkeleton = subcategoriesLoading && !hasSubcategories
        
        // Show subcategories if:
        // 1. Not on feed URL, OR
        // 2. On feed URL with subcategories to show (tags or featured categories)
        // 3. Subcategories are loading (show skeleton)
        const isOnFeed = location.pathname === feedUrl()
        const shouldShowSubcategories = !isOnFeed || ((fullPath === '/tags' || fullPath === '/markets') && (hasSubcategories || subcategoriesLoading))
        
        // Only render subcategories section if we should show them
        if (!shouldShowSubcategories || (!hasSubcategories && !subcategoriesLoading && categories && categories.length > 0)) {
          return null
        }
        
        const renderSubcategoriesSkeleton = () => (
          <div className="flex gap-2 px-4 md:px-8 overflow-x-auto scrollbar-hide">
            {[72, 90, 60, 85, 55, 70].map((w, index) => (
              <div
                key={`subcategory-skeleton-${index}`}
                className="relative overflow-hidden py-3 px-4 rounded-lg bg-surface-2 flex-shrink-0">
                <div className="h-4 rounded" style={{ width: `${w}px` }}></div>
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-3/20 to-transparent"
                  style={{ animation: 'shimmer 1.5s infinite', transform: 'translateX(-100%)' }}
                />
              </div>
            ))}
          </div>
        )
         
        return (
          <div
            className={`category-navigation category-navigation--subcategories relative z-[9] min-h-12 md:!mb-4 md:!mt-4 ${
              !isVisible ? 'category-navigation--hidden -translate-y-full' : 'translate-y-0'
            }`}>
            {shouldShowSubcategoriesSkeleton ? (
              renderSubcategoriesSkeleton()
            ) : hasSubcategories ? (
              <Slider
                type="horizontal-scroll"
                settings={{
                  ...sliderSettings,
                  spaceBetween: 8,
                }}
                name="subcategory-nav-slider">
                {subcats.map((item, index) => (
                  <div key={`subnav-slide-${index}`} className="swiper-slide" style={{ width: 'auto' }}>
                    {item}
                  </div>
                ))}
              </Slider>
            ) : null}
          </div>
        )
      })()}
    </>
  )
}

CategoryNavigation.propTypes = {
  parent: PropTypes.objectOf(
    PropTypes.objectOf({ tree: PropTypes.arrayOf({ title: PropTypes.string, href: PropTypes.string }) })
  ),
  subcategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      href: PropTypes.string,
    })
  ),
}

export default CategoryNavigation
